import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Lock, ArrowRight, User, Dumbbell, Apple, Building2, Store } from "lucide-react";
import { useAuthStore } from "../../store/authStore";
import { IMG } from "../../utils/images";

const slides = [
  {
    img: IMG.authFood,
    title: "Know Your Food",
    sub: "AI-powered food scanning and nutrition tracking, built for Indian meals.",
  },
  {
    img: IMG.authTrainer,
    title: "Train With The Best",
    sub: "Connect with verified Indian fitness coaches and dietitians.",
  },
];

export default function Login() {
  const navigate = useNavigate();
  const loc = useLocation();
  // If we were redirected here by ProtectedRoute it will have appended
  // `?next=<encoded path>` — send the user there after a successful auth.
  // Only accept internal paths (leading "/") to avoid open-redirect attacks.
  const nextParam = (() => {
    try {
      const raw = new URLSearchParams(loc.search).get("next");
      if (!raw) return null;
      const decoded = decodeURIComponent(raw);
      return decoded.startsWith("/") && !decoded.startsWith("//") ? decoded : null;
    } catch {
      return null;
    }
  })();
  const { login, register, loading, error } = useAuthStore();
  const [slide, setSlide] = useState(0);
  const [mode, setMode] = useState("login"); // login | register
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setSlide((s) => (s + 1) % slides.length), 4500);
    return () => clearInterval(t);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (mode === "register") {
        if (!ageConfirmed) {
          useAuthStore.setState({ error: "Please confirm you are 13 years or older" });
          return;
        }
        if (!termsAccepted) {
          useAuthStore.setState({ error: "Please accept the Terms and Privacy Policy" });
          return;
        }
      }
      const user =
        mode === "login"
          ? await login(email, password)
          : await register(name || email.split("@")[0], email, password, {
              age_confirmed: ageConfirmed,
              terms_accepted: termsAccepted,
              marketing_consent: marketingConsent,
            });
      if (!user.onboarding_complete) {
        navigate("/onboarding", { replace: true });
      } else if (nextParam) {
        navigate(nextParam, { replace: true });
      } else if (user.is_superadmin) {
        navigate("/superadmin", { replace: true });
      } else if (user.role === "admin") {
        navigate("/admin/professionals", { replace: true });
      } else {
        // Role-aware landing — fall back to /home if the roles endpoint fails
        // for any reason so we never strand a fresh sign-in.
        try {
          const { apiClient } = await import("../../services/api");
          const { data } = await apiClient.get("/me/roles");
          const primary = data?.primary;
          const roleHome = {
            trainer: "/pro/portal",
            nutritionist: "/pro/portal",
            gym_owner: "/arena/dashboard",
            vendor: "/marketplace/sell",
          }[primary];
          navigate(roleHome || "/home", { replace: true });
        } catch {
          navigate("/home", { replace: true });
        }
      }
    } catch (err) {
      // authStore already sets `error`. Nothing else to do here.
    }
  };

  const googleLogin = () => {
    // Direct Google OAuth — uses FitFrik's own GOOGLE_CLIENT_ID + GOOGLE_CLIENT_SECRET.
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const backend = process.env.REACT_APP_BACKEND_URL || "http://127.0.0.1:8000";
    window.location.href = `${backend}/api/auth/google/login`;
  };

  return (
    <div className="min-h-screen lg:flex" data-testid="login-page">
      {/* LEFT: carousel */}
      <div className="relative lg:w-[55%] h-[40vh] lg:h-screen overflow-hidden bg-black">
        <AnimatePresence>
          <motion.div
            key={slide}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="absolute inset-0"
          >
            <img src={slides[slide].img} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 60%, rgba(0,0,0,0.85) 100%)" }} />
            <div className="absolute bottom-10 left-8 right-8 max-w-lg text-white">
              <h2 className="font-display text-[32px] sm:text-[40px] leading-tight">{slides[slide].title}</h2>
              <p className="mt-2 text-sm sm:text-[15px] text-white/80">{slides[slide].sub}</p>
            </div>
          </motion.div>
        </AnimatePresence>
        {/* dots */}
        <div className="absolute bottom-4 left-8 flex gap-1.5">
          {slides.map((_, i) => (
            <div
              key={i}
              className="h-1.5 rounded-full transition-all"
              style={{
                width: i === slide ? 24 : 6,
                background: i === slide ? "#fff" : "rgba(255,255,255,0.5)",
              }}
            />
          ))}
        </div>
      </div>

      {/* RIGHT: form */}
      <div className="lg:w-[45%] flex items-start lg:items-center justify-center bg-canvas">
        <div className="w-full max-w-md px-6 sm:px-8 py-10 lg:py-0">
          <div className="flex flex-col mb-10">
            <span
              style={{
                fontFamily: "'Barlow Condensed', sans-serif",
                fontWeight: 800,
                fontSize: 64,
                letterSpacing: "-1px",
                lineHeight: 1,
                display: "inline-flex",
                alignItems: "baseline",
              }}
              data-testid="brand-wordmark"
            >
              <span style={{ color: "#00D4FF" }}>F</span>
              <span style={{ color: "#1A1A1A" }}>itFrik</span>
            </span>
            <span
              style={{
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontWeight: 500,
                fontSize: 13,
                marginTop: 6,
                color: "#5B5B5B",
              }}
              data-testid="brand-tagline"
            >
              India&apos;s fitness super-app
            </span>
          </div>

          <h1 className="font-display text-[32px] leading-tight">
            {mode === "login" ? "Welcome back" : "Create your account"}
          </h1>
          <p className="mt-1 text-sm text-ink-soft">
            {mode === "login" ? "Sign in to continue your training." : "Start tracking, training, and transforming."}
          </p>

          <form onSubmit={submit} className="mt-7 space-y-3">
            {mode === "register" && (
              <label className="block">
                <span className="text-[12px] text-ink-muted uppercase tracking-wider">Name</span>
                <div className="mt-1.5 flex items-center gap-2 bg-white rounded-[12px] px-3 h-12 border border-line">
                  <User size={18} className="text-ink-muted" />
                  <input
                    data-testid="register-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    className="flex-1 bg-transparent outline-none text-[15px]"
                    required
                  />
                </div>
              </label>
            )}
            <label className="block">
              <span className="text-[12px] text-ink-muted uppercase tracking-wider">Email</span>
              <div className="mt-1.5 flex items-center gap-2 bg-white rounded-[12px] px-3 h-12 border border-line">
                <Mail size={18} className="text-ink-muted" />
                <input
                  data-testid="login-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  type="email"
                  className="flex-1 bg-transparent outline-none text-[15px]"
                  required
                />
              </div>
            </label>
            <label className="block">
              <span className="text-[12px] text-ink-muted uppercase tracking-wider">Password</span>
              <div className="mt-1.5 flex items-center gap-2 bg-white rounded-[12px] px-3 h-12 border border-line">
                <Lock size={18} className="text-ink-muted" />
                <input
                  data-testid="login-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  type="password"
                  className="flex-1 bg-transparent outline-none text-[15px]"
                  required
                  minLength={6}
                />
              </div>
            </label>

            {error && (
              <div className="text-sm text-red-500" data-testid="login-error">{error}</div>
            )}

            {mode === "register" && (
              <div className="mt-2 space-y-2">
                <label className="flex gap-2 items-start text-[12px] text-ink-muted cursor-pointer">
                  <input
                    type="checkbox"
                    checked={ageConfirmed}
                    onChange={(e) => setAgeConfirmed(e.target.checked)}
                    data-testid="reg-age-confirmed"
                    style={{ marginTop: 2 }}
                  />
                  <span>I am 13 years or older.</span>
                </label>
                <label className="flex gap-2 items-start text-[12px] text-ink-muted cursor-pointer">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    data-testid="reg-terms-accepted"
                    style={{ marginTop: 2 }}
                  />
                  <span>
                    I agree to FitFrik&apos;s{" "}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: "#00D4FF", textDecoration: "underline" }}>Terms</a>
                    {" "}and{" "}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "#00D4FF", textDecoration: "underline" }}>Privacy Policy</a>.
                  </span>
                </label>
                <label className="flex gap-2 items-start text-[12px] text-ink-muted cursor-pointer">
                  <input
                    type="checkbox"
                    checked={marketingConsent}
                    onChange={(e) => setMarketingConsent(e.target.checked)}
                    data-testid="reg-marketing-consent"
                    style={{ marginTop: 2 }}
                  />
                  <span>Send me fitness tips and offers. <span className="text-ink-muted">(optional)</span></span>
                </label>
              </div>
            )}

            <button
              type="submit"
              data-testid="login-submit"
              disabled={loading}
              className="mt-2 w-full h-13 grid place-items-center rounded-pill font-medium transition-all disabled:opacity-50"
              style={{ background: "var(--accent-blue)", color: "#000", height: 52, borderRadius: 100 }}
            >
              <span className="flex items-center gap-2">
                {mode === "login" ? "Sign in" : "Create account"} <ArrowRight size={18} />
              </span>
            </button>
          </form>

          {mode === "login" && (
            <div style={{ marginTop: 10, textAlign: "right" }}>
              <a
                href="/forgot-password"
                data-testid="forgot-password-link"
                style={{ fontSize: 12, color: "var(--text-secondary,#4A4845)" }}
              >
                Forgot password?
              </a>
            </div>
          )}

          <div className="my-5 flex items-center gap-3 text-[12px] text-ink-muted">
            <div className="flex-1 h-px bg-line" /> or <div className="flex-1 h-px bg-line" />
          </div>

          <button
            data-testid="google-login"
            onClick={googleLogin}
            type="button"
            className="w-full h-12 flex items-center justify-center gap-2 bg-white rounded-pill border border-line hover:bg-canvas2 transition-colors"
            style={{ borderRadius: 100 }}
          >
            <svg width="18" height="18" viewBox="0 0 18 18">
              <path fill="#4285F4" d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
              <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
              <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
            </svg>
            <span className="text-[14px] font-medium text-ink">Continue with Google</span>
          </button>

          <div className="mt-6 text-center text-sm">
            {mode === "login" ? (
              <>
                <span className="text-ink-soft">New to FitFrik? </span>
                <button
                  type="button"
                  onClick={() => setMode("register")}
                  data-testid="switch-to-register"
                  className="text-ink font-medium underline-offset-4 hover:underline"
                >
                  Start free
                </button>
              </>
            ) : (
              <>
                <span className="text-ink-soft">Already have an account? </span>
                <button
                  type="button"
                  onClick={() => setMode("login")}
                  data-testid="switch-to-login"
                  className="text-ink font-medium underline-offset-4 hover:underline"
                >
                  Sign in
                </button>
              </>
            )}
          </div>

          <div className="mt-8 flex justify-center gap-5 text-[12px] text-ink-muted">
            <Link to="/trainers" className="hover:text-ink">Browse Trainers</Link>
            <Link to="/nutrition/scanner" className="hover:text-ink">Try Food Scanner</Link>
          </div>

          {/* Compact "sign up as" pill row — one line, 4 pro paths.
              Every path routes into its own onboarding; the account role is set
              during that flow, so logins are already role-differentiated. */}
          <div className="mt-6" data-testid="pro-signup-row">
            <div
              style={{
                textAlign: "center",
                fontFamily: "'Plus Jakarta Sans', sans-serif",
                fontSize: 10,
                letterSpacing: "0.16em",
                textTransform: "uppercase",
                color: "#8A8884",
                fontWeight: 700,
                marginBottom: 8,
              }}
            >
              Sign up as
            </div>
            <div className="flex justify-center gap-2 flex-wrap">
              <RolePill Icon={Dumbbell}  label="Trainer"      to="/pro/apply?type=trainer"           color="#00B0D5" testid="pro-signup-trainer" />
              <RolePill Icon={Apple}     label="Nutritionist" to="/pro/apply?type=nutritionist"      color="#7A67D5" testid="pro-signup-nutri" />
              <RolePill Icon={Building2} label="Gym"          to="/arena/join"                       color="#B58A00" testid="pro-signup-gym" />
              <RolePill Icon={Store}     label="Vendor"       to="/marketplace/sell/brand-apply"     color="#D85825" testid="pro-signup-vendor" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RolePill({ Icon, label, to, color, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "6px 12px",
        borderRadius: 100,
        background: "#FFFFFF",
        border: "1px solid #E7E5E1",
        fontFamily: "'Plus Jakarta Sans', sans-serif",
        fontSize: 12,
        fontWeight: 600,
        color: "#1A1A1A",
        textDecoration: "none",
        whiteSpace: "nowrap",
      }}
    >
      <Icon size={13} color={color} strokeWidth={2.4} />
      {label}
    </Link>
  );
}
